import "../../site-chrome.css";
import { initSiteChrome } from "../../site-chrome";
import "./style.css";
import "./polish.css";
import { importKey, scanGuestLibrary, type LibraryItem } from "./library";

declare global {
  interface Window {
    turnstile?: {
      render(target: string | HTMLElement, options: Record<string, unknown>): string;
      reset(widgetId?: string): void;
    };
  }
}

const apiOrigin = (import.meta.env.VITE_MEMBER_API_ORIGIN || "https://api.tgr.tw").replace(/\/$/, "");
const accountsOrigin = (import.meta.env.VITE_ACCOUNTS_ORIGIN || "https://accounts.tgr.tw").replace(/\/$/, "");
const siteOrigin = (import.meta.env.VITE_SITE_ORIGIN || "https://www.tgr.tw").replace(/\/$/, "");
const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY || "";

interface ApiEnvelope<T> { data: T; request_id: string; schema_version: 1 }
interface Me { id: string; email: string; email_verified: boolean; display_name: string | null; language: "zh-TW" | "en"; newsletter_status: string }
interface Preference { newsletter_status: string; newsletter_consent: boolean; topics: string[]; suppression_reason: string | null; sync_pending: boolean }

let me: Me | null = null;
let currentPreference: Preference | null = null;
let deletionTurnstileToken = "";
let deletionWidgetId = "";

function loadTurnstile(): void {
  if (typeof window.turnstile?.render === "function" || document.querySelector("script[data-tgr-turnstile]")) return;
  if ("turnstile" in window) {
    try { delete window.turnstile; } catch { /* A non-configurable collision will fail closed below. */ }
  }
  const script = document.createElement("script");
  script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
  script.async = true;
  script.defer = true;
  script.dataset.tgrTurnstile = "";
  document.head.appendChild(script);
}

function clearMemberCache(): void {
  for (let index = localStorage.length - 1; index >= 0; index -= 1) {
    const key = localStorage.key(index);
    if (key?.startsWith("tgr-member-")) localStorage.removeItem(key);
  }
  for (let index = sessionStorage.length - 1; index >= 0; index -= 1) {
    const key = sessionStorage.key(index);
    if (key?.startsWith("tgr-")) sessionStorage.removeItem(key);
  }
}

function renderDeletionTurnstile(attempt = 0): void {
  const button = document.querySelector<HTMLButtonElement>("#request-deletion");
  if (!siteKey) {
    if (button) button.disabled = true;
    return;
  }
  const turnstile = window.turnstile;
  if (typeof turnstile?.render !== "function") {
    if (attempt >= 100) {
      if (button) button.disabled = true;
      setGlobalStatus("安全驗證暫時無法載入。", "error");
      return;
    }
    window.setTimeout(() => renderDeletionTurnstile(attempt + 1), 100);
    return;
  }
  deletionWidgetId = turnstile.render("#deletion-turnstile", {
    sitekey: siteKey,
    action: "account_deletion",
    language: "zh-tw",
    callback: (token: string) => { deletionTurnstileToken = token; },
    "expired-callback": () => { deletionTurnstileToken = ""; },
    "error-callback": () => {
      deletionTurnstileToken = "";
      setGlobalStatus("安全驗證暫時無法使用。", "error");
    },
  });
}

function setGlobalStatus(message: string, kind: "info" | "error" | "success" = "info"): void {
  const box = document.querySelector<HTMLElement>("#global-status");
  if (!box) return;
  box.textContent = message;
  box.dataset.kind = kind;
  box.hidden = !message;
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${apiOrigin}${path}`, {
    ...init,
    credentials: "include",
    headers: { "content-type": "application/json", ...(init.headers || {}) },
  });
  if (response.status === 401) {
    clearMemberCache();
    const login = new URL(accountsOrigin + "/");
    login.searchParams.set("callback", window.location.href);
    window.location.assign(login.toString());
    throw new Error("AUTH_REQUIRED");
  }
  const payload = await response.json().catch(() => null) as ApiEnvelope<T> | { error?: { code?: string; message?: string } } | null;
  if (!response.ok || !payload || !("data" in payload)) {
    const message = payload && "error" in payload ? payload.error?.message : undefined;
    throw new Error(message || "REQUEST_FAILED");
  }
  return payload.data;
}

function showSection(name: string): void {
  document.querySelectorAll<HTMLElement>(".panel").forEach((panel) => {
    const active = panel.id === `section-${name}`;
    panel.hidden = !active;
    panel.classList.toggle("active", active);
  });
  document.querySelectorAll<HTMLButtonElement>("[data-section]").forEach((button) => {
    button.classList.toggle("active", button.dataset.section === name);
  });
  document.querySelector<HTMLElement>("#content")?.focus();
  history.replaceState(null, "", `#${name}`);
}

function statusLabel(status: string): string {
  return ({
    not_subscribed: "未訂閱",
    pending: "同步中",
    active: "已啟用",
    unsubscribed: "已退訂",
    bounced: "退信停用",
    complained: "申訴停用",
  } as Record<string, string>)[status] || status;
}

function renderMe(data: Me): void {
  const email = document.querySelector<HTMLElement>("#account-email");
  const newsletter = document.querySelector<HTMLElement>("#account-newsletter");
  const displayName = document.querySelector<HTMLInputElement>("#display-name");
  const language = document.querySelector<HTMLSelectElement>("#language");
  if (email) email.textContent = data.email;
  if (newsletter) newsletter.textContent = statusLabel(data.newsletter_status);
  if (displayName) displayName.value = data.display_name || "";
  if (language) language.value = data.language;
}

function renderPreference(data: Preference): void {
  currentPreference = data;
  const consent = document.querySelector<HTMLInputElement>("#newsletter-consent");
  const status = document.querySelector<HTMLElement>("#newsletter-status");
  if (consent) consent.checked = data.newsletter_consent;
  if (status) status.textContent = statusLabel(data.newsletter_status);
  document.querySelectorAll<HTMLInputElement>('input[name="topic"]').forEach((input) => {
    input.checked = data.topics.includes(input.value);
  });
}

async function loadLibrary(): Promise<void> {
  const result = await api<{ items: LibraryItem[] }>("/v1/library");
  const list = document.querySelector<HTMLElement>("#library-list");
  const empty = document.querySelector<HTMLElement>("#library-empty");
  if (!list || !empty) return;
  empty.hidden = result.items.length > 0;
  list.replaceChildren(...result.items.map((item) => {
    const article = document.createElement("article");
    article.className = "library-item";
    const link = document.createElement("a");
    link.href = `${siteOrigin}/p/${encodeURIComponent(item.article_id)}.html`;
    link.textContent = item.article_id.replaceAll("-", " ");
    const meta = document.createElement("span");
    meta.textContent = [item.bookmarked ? "已收藏" : "", item.read_at ? "已讀" : ""].filter(Boolean).join(" · ") || "閱讀紀錄";
    article.append(link, meta);
    return article;
  }));
}

async function offerGuestMerge(): Promise<void> {
  const guest = scanGuestLibrary(localStorage);
  const box = document.querySelector<HTMLElement>("#merge-box");
  const summary = document.querySelector<HTMLElement>("#merge-summary");
  if (!box || !summary || !guest.length || sessionStorage.getItem("tgr-merge-dismissed") === "1") return;
  const bookmarks = guest.filter((item) => item.bookmarked).length;
  const reads = guest.filter((item) => item.read_at).length;
  summary.textContent = `${bookmarks} 篇收藏、${reads} 篇已讀。合併採集合聯集，不會刪除訪客資料。`;
  box.hidden = false;
  document.querySelector<HTMLButtonElement>("#merge-library")?.addEventListener("click", async () => {
    const button = document.querySelector<HTMLButtonElement>("#merge-library");
    if (button) button.disabled = true;
    try {
      const result = await api<{ imported: number; duplicate: boolean }>("/v1/library/import", {
        method: "POST",
        body: JSON.stringify({ import_key: await importKey(guest), items: guest }),
      });
      box.hidden = true;
      setGlobalStatus(result.duplicate ? "這批訪客資料先前已合併。" : `已合併 ${result.imported} 筆閱讀資料。`, "success");
      await loadLibrary();
    } catch {
      setGlobalStatus("合併失敗，訪客資料仍保留，稍後可重試。", "error");
      if (button) button.disabled = false;
    }
  });
  document.querySelector<HTMLButtonElement>("#dismiss-merge")?.addEventListener("click", () => {
    sessionStorage.setItem("tgr-merge-dismissed", "1");
    box.hidden = true;
  });
}

async function initialize(): Promise<void> {
  try {
    [me, currentPreference] = await Promise.all([api<Me>("/v1/me"), api<Preference>("/v1/preferences")]);
    renderMe(me);
    renderPreference(currentPreference);
    await Promise.all([loadLibrary(), offerGuestMerge()]);
    const token = new URL(window.location.href).searchParams.get("token");
    if (token && window.location.pathname.startsWith("/privacy/delete")) {
      showSection("privacy");
      const confirm = document.querySelector<HTMLElement>("#delete-confirm");
      if (confirm) confirm.hidden = false;
    } else {
      const routeSection = window.location.pathname.startsWith("/newsletter")
        ? "newsletter"
        : window.location.pathname.startsWith("/library")
          ? "library"
          : "overview";
      showSection(window.location.hash.slice(1) || routeSection);
    }
  } catch (error) {
    if (error instanceof Error && error.message !== "AUTH_REQUIRED") setGlobalStatus("會員資料載入失敗，請稍後重試。", "error");
  }
}

document.querySelectorAll<HTMLButtonElement>("[data-section]").forEach((button) => {
  button.addEventListener("click", () => showSection(button.dataset.section || "overview"));
});

document.querySelector<HTMLFormElement>("#profile-form")?.addEventListener("submit", async (event) => {
  event.preventDefault();
  try {
    await api("/v1/me", {
      method: "PATCH",
      body: JSON.stringify({
        display_name: document.querySelector<HTMLInputElement>("#display-name")?.value.trim() || null,
        language: document.querySelector<HTMLSelectElement>("#language")?.value || "zh-TW",
      }),
    });
    setGlobalStatus("個人資料已儲存。", "success");
  } catch {
    setGlobalStatus("個人資料未能儲存。", "error");
  }
});

document.querySelector<HTMLFormElement>("#newsletter-form")?.addEventListener("submit", async (event) => {
  event.preventDefault();
  const consent = Boolean(document.querySelector<HTMLInputElement>("#newsletter-consent")?.checked);
  const topics = [...document.querySelectorAll<HTMLInputElement>('input[name="topic"]:checked')].map((input) => input.value);
  try {
    const result = await api<Preference>("/v1/preferences", {
      method: "PUT",
      body: JSON.stringify({ newsletter_consent: consent, topics }),
    });
    renderPreference({ ...currentPreference!, ...result, newsletter_consent: consent, suppression_reason: null });
    setGlobalStatus(result.sync_pending ? "偏好已儲存，正在同步電子報服務。" : "偏好已儲存。", "success");
  } catch (error) {
    setGlobalStatus(error instanceof Error ? error.message : "電子報偏好未能儲存。", "error");
  }
});

document.querySelector<HTMLButtonElement>("#logout")?.addEventListener("click", async () => {
  try { await fetch(`${apiOrigin}/api/auth/sign-out`, { method: "POST", credentials: "include" }); } catch { /* Session still expires server-side. */ }
  clearMemberCache();
  window.location.assign(accountsOrigin + "/");
});

document.querySelector<HTMLButtonElement>("#request-deletion")?.addEventListener("click", async () => {
  if (!deletionTurnstileToken) {
    setGlobalStatus("請先完成刪除帳號安全驗證。", "error");
    return;
  }
  try {
    await api("/v1/me/deletion-challenge", {
      method: "POST",
      body: JSON.stringify({ turnstileToken: deletionTurnstileToken }),
    });
    setGlobalStatus("若此次要求可使用，我們會寄出十分鐘有效的刪除驗證信。", "success");
  } catch {
    setGlobalStatus("目前無法寄出刪除驗證信。", "error");
  } finally {
    deletionTurnstileToken = "";
    if (typeof window.turnstile?.reset === "function" && deletionWidgetId) window.turnstile.reset(deletionWidgetId);
  }
});

document.querySelector<HTMLButtonElement>("#confirm-deletion")?.addEventListener("click", async () => {
  const token = new URL(window.location.href).searchParams.get("token");
  if (!token) return;
  try {
    await api("/v1/me", { method: "DELETE", body: JSON.stringify({ reauth_token: token }) });
    clearMemberCache();
    window.location.assign(`${siteOrigin}/?member_deleted=1`);
  } catch (error) {
    setGlobalStatus(error instanceof Error ? error.message : "帳號尚未刪除。", "error");
  }
});

loadTurnstile();
renderDeletionTurnstile();
initSiteChrome({ siteOrigin, membersOrigin: window.location.origin });
initialize();
